
import { DataStore } from '@aws-amplify/datastore';
import type { DataItem as DataItemType, User as UserType, Report as ReportType, AnalyticsData as AnalyticsDataType, ActivityLog as ActivityLogType, SafetyForm as SafetyFormType } from '../models/index.js';
import { DataItem as DataItemModel, User as UserModel, Report as ReportModel, AnalyticsData as AnalyticsDataModel, ActivityLog as ActivityLogModel, SafetyForm as SafetyFormModel } from '../models/index.js';
import type { MutableModel } from '@aws-amplify/datastore';

/**
 * Amplify DataStore Service
 * Handles all data operations with automatic offline sync
 */
export class AmplifyDataService {
  /**
   * Stop DataStore
   */
  static async stop() {
    try {
      await DataStore.stop();
      console.log('DataStore stopped');
    } catch (error) {
      console.error('Error stopping DataStore:', error);
    }
  }

  /**
   * Start DataStore (used when DataStore was stopped previously)
   * Returns true if started, false otherwise
   */
  static async start(): Promise<boolean> {
    try {
      await DataStore.start();
      console.log('DataStore started');
      return true;
    } catch (error) {
      console.error('Error starting DataStore:', error);
      return false;
    }
  }

  /**
   * Clear all local data
   */
  static async clear() {
    try {
      await DataStore.clear();
      console.log('DataStore cleared');
    } catch (error) {
      console.error('Error clearing DataStore:', error);
    }
  }

  /**
   * Get sync status
   */
  static async getSyncStatus() {
    // DataStore automatically handles sync, but we can check network status
    return {
      isOnline: navigator.onLine,
      // DataStore doesn't expose sync queue directly, but syncs automatically
    };
  }
}

/**
 * DataItem Operations
 */
export const dataItemService = {
  async getAll() {
    return await DataStore.query(DataItemModel);
  },

  async getById(id: string) {
    return await DataStore.query(DataItemModel, id);
  },

  async create(data: Partial<DataItemType>) {
    const item = new DataItemModel({
      name: data.name || '',
      description: data.description || '',
      status: data.status || 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
      ...data,
    });
    return await DataStore.save(item);
  },

  async update(id: string, updates: Partial<DataItemType>) {
    const original = await DataStore.query(DataItemModel, id);
    if (!original) {
      throw new Error('DataItem not found');
    }
    return await DataStore.save(
      DataItemModel.copyOf(original, (updated: MutableModel<DataItemType>) => {
        Object.assign(updated, updates);
        updated.updatedAt = new Date().toISOString();
        updated.version = (updated.version || 1) + 1;
      })
    );
  },

  async delete(id: string) {
    const item = await DataStore.query(DataItemModel, id);
    if (item) {
      await DataStore.delete(item);
    }
  },
};

/**
 * User Operations
 */
export const userService = {
  async getAll() {
    return await DataStore.query(UserModel);
  },

  async getById(id: string) {
    return await DataStore.query(UserModel, id);
  },

  async create(data: Partial<UserType>) {
    const user = new UserModel({
      name: data.name || '',
      email: data.email || '',
      role: data.role || 'user',
      status: data.status || 'active',
      lastLogin: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      ...data,
    });
    return await DataStore.save(user);
  },

  async update(id: string, updates: Partial<UserType>) {
    const original = await DataStore.query(UserModel, id);
    if (!original) {
      throw new Error('User not found');
    }
    return await DataStore.save(
      UserModel.copyOf(original, (updated: MutableModel<UserType>) => {
        Object.assign(updated, updates);
      })
    );
  },

  async delete(id: string) {
    const user = await DataStore.query(UserModel, id);
    if (user) {
      await DataStore.delete(user);
    }
  },
};

/**
 * Report Operations
 */
export const reportService = {
  async getAll() {
    return await DataStore.query(ReportModel);
  },

  async getById(id: string) {
    return await DataStore.query(ReportModel, id);
  },

  async create(data: Partial<ReportType>) {
    const report = new ReportModel({
      title: data.title || '',
      type: data.type || '',
      generatedAt: new Date().toISOString(),
      status: data.status || 'pending',
      data: (data as any).data || '{}',
      ...data,
    });
    return await DataStore.save(report);
  },
};

/**
 * Analytics Operations
 */
export const analyticsService = {
  async getAll() {
    return await DataStore.query(AnalyticsDataModel);
  },

  async create(data: Partial<AnalyticsDataType>) {
    const analytics = new AnalyticsDataModel({
      metric: data.metric || '',
      value: data.value || 0,
      timestamp: new Date().toISOString(),
      category: data.category || '',
      ...data,
    });
    return await DataStore.save(analytics);
  },
};

/**
 * Activity Log Operations
 */
export const activityService = {
  async getRecent(limit: number = 20) {
    const activities = await DataStore.query(ActivityLogModel);
    return activities
      .sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      })
      .slice(0, limit);
  },

  async create(data: Partial<ActivityLogType>) {
    const activity = new ActivityLogModel({
      description: data.description || '',
      timestamp: new Date().toISOString(),
      userId: data.userId || '',
      action: data.action || '',
      ...data,
    });
    return await DataStore.save(activity);
  },
};

/**
 * Safety Form Operations
 */
export const safetyFormService = {
  async getAll() {
    return await DataStore.query(SafetyFormModel);
  },

  async getById(id: string) {
    return await DataStore.query(SafetyFormModel, id);
  },

  async create(data: Partial<SafetyFormType>) {
    // CRITICAL: Get current user ID to set owner field
    // This ensures items created offline can sync when user logs back in
    let owner: string | undefined = undefined;
    try {
        const { getCurrentUser } = await import('@aws-amplify/auth');
      const user = await getCurrentUser();
      owner =
        user?.username || (user as any)?.attributes?.sub || (user as any)?.attributes?.email || undefined;
    } catch (e) {
      // User not authenticated - owner will be set when they log in
      console.log('   ℹ️ User not authenticated - owner will be set on next login');
    }
    
    const form = new SafetyFormModel({
      formType: data.formType || 'SafetyChecklist',
      requiredPermits: data.requiredPermits || [],
      requiredChecklist: data.requiredChecklist || [],
      ppe: data.ppe || '{}',
      hazards: data.hazards || [],
      controlMeasures: data.controlMeasures || [],
      tasks: data.tasks || [],
      emergencyActionPlan: data.emergencyActionPlan || false,
      emergencyMusterArea: data.emergencyMusterArea || '',
      emergencyContacts: data.emergencyContacts || '{}',
      teamMembers: data.teamMembers || [],
      status: data.status || 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...(owner ? { owner } : {}), // Set owner if available
      ...data,
    });
    
    console.log('📝 [CREATE] SafetyForm in DataStore:', {
      formType: form.formType,
      status: form.status,
      projectName: form.projectName,
      timestamp: new Date().toISOString(),
    });
    
    try {
      const saved = await DataStore.save(form);
      
      const version = (saved as any)._version;
      const lastChanged = (saved as any)._lastChangedAt;
      
      console.log('✅ [SAVED] SafetyForm saved to DataStore:', {
        id: saved.id,
        version: version || 'N/A (local only)',
        lastChanged: lastChanged || 'N/A',
        synced: version ? '✅ Will sync to DynamoDB' : '⏳ Local only (will sync when online)',
      });
      
      if (!version) {
        console.log('   ℹ️ Item has no version yet - this is normal for new items');
        console.log('   ℹ️ Version will be assigned when sync completes');
        console.log('   ℹ️ Check DynamoDB in 10-30 seconds');
      } else {
        console.log('   ✅ Item has version - sync is in progress or complete');
      }
      
      // Log sync operation
      const { logSyncOperation } = await import('../utils/syncLogger');
      logSyncOperation('CREATE', 'SafetyForm', saved.id, data);
      
      return saved;
    } catch (error: any) {
      console.error('❌ [ERROR] Failed to save SafetyForm:', error);
      console.error('   Message:', error?.message || error);
      throw error;
    }
  },

  async update(id: string, updates: Partial<SafetyFormType>) {
    console.log('📝 [UPDATE] SafetyForm in DataStore:', {
      id,
      updates: Object.keys(updates).join(', '),
      timestamp: new Date().toISOString(),
    });
    
    const original = await DataStore.query(SafetyFormModel, id);
    if (!original) {
      throw new Error('SafetyForm not found');
    }
    
    try {
      const updated = await DataStore.save(
        SafetyFormModel.copyOf(original, (updated: MutableModel<SafetyFormType>) => {
          // Use Object.assign to copy all updates, including owner field
          Object.assign(updated, updates);
          // Ensure updatedAt is always set
          updated.updatedAt = new Date().toISOString();
          // If owner is in updates, explicitly set it (DataStore may ignore it otherwise)
          if ((updates as any).owner) {
            (updated as any).owner = (updates as any).owner;
          }
        })
      );
      
      const version = (updated as any)._version;
      console.log('✅ [UPDATED] SafetyForm updated in DataStore:', {
        id: updated.id,
        version: version || 'N/A',
        synced: version ? '✅ Will sync to DynamoDB' : '⏳ Local only',
      });
      
      // Log sync operation
      const { logSyncOperation } = await import('../utils/syncLogger');
      logSyncOperation('UPDATE', 'SafetyForm', id, updates);
      
      return updated;
    } catch (error: any) {
      console.error('❌ [ERROR] Failed to update SafetyForm:', error);
      throw error;
    }
  },

  async delete(id: string) {
    const form = await DataStore.query(SafetyFormModel, id);
    if (form) {
      await DataStore.delete(form);
    }
  },
};
